import { BusinessUnit } from '@Comments/models/BusinessUnit';
import { SortOrder } from '@Comments/models/SortOrder';
import { Userdata } from '@Comments/models/Userdata';
import { FrontendThread } from '@Comments/models/FrontendThread';
import { ViewPublicComment } from '@Comments/openapi/client/index';
import { AppState } from '@Comments/models/AppState';
import { LoginProvider } from '@Comments/models/LoginProvider';

// eslint-disable-next-line no-unused-vars
export type Dispatch = (action: Action) => void;

export type State = {
  appState: AppState;
  env: {
    bu: BusinessUnit;
    base: string;
    threadUrn: string;
    loginProvider: LoginProvider;
    isSrgLoginSupported: boolean;
  };
  user: Userdata;
  layout: {
    isExpanded: boolean;
  };
  thread: FrontendThread;
  commentsCount: number;
  comments: ViewPublicComment[];
  nextToken: number;
  limit: number;
  hasMoreComments: boolean;
  sortOrder: SortOrder;
  activeReplyForm: string | null;
  activeReplyFormAfterLogin: string | null;
};
export const INITIAL_CONTEXT: State = {
  appState: 'loading',
  env: {
    bu: 'srf',
    base: '',
    threadUrn: '',
    loginProvider: 'janrain',
    isSrgLoginSupported: true,
  },
  user: {
    name: '',
    token: '',
    isLoggedIn: false,
  },
  layout: {
    isExpanded: true,
  },
  thread: {
    goodbyeMessage: '',
    welcomeMessage: '',
    authorUsername: '',
    state: 'closed',
  },
  commentsCount: 0,
  comments: [],
  nextToken: 0,
  limit: 3,
  hasMoreComments: false,
  sortOrder: 'created',
  activeReplyForm: null,
  activeReplyFormAfterLogin: null,
};

export type Action =
  | {
      type: 'update-comments';
      data: {
        comments: ViewPublicComment[];
        nextToken: number;
        limit: number;
        hasMoreComments: boolean;
        appState: AppState;
      };
    }
  | {
      type: 'update-app-state';
      data: AppState;
    }
  | {
      type: 'update-sort-order';
      data: SortOrder;
    }
  | {
      type: 'login-user';
      data: {
        userName: string;
        accessToken: string;
        activeReplyForm: string | null;
      };
    }
  | {
      type: 'logout-user';
    }
  | {
      type: 'set-expand';
      data: boolean;
    }
  | {
      type: 'update-thread';
      data: FrontendThread;
    }
  | {
      type: 'update-active-reply-form';
      data: string | null;
    }
  | {
      type: 'update-active-reply-form-after-login';
      data: string | null;
    }
  | {
      type: 'set-srg-login-supported';
      data: boolean;
    };

export const appContextReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'update-comments': {
      return {
        ...state,
        comments: action.data.comments,
        nextToken: action.data.nextToken,
        limit: action.data.limit,
        hasMoreComments: action.data.hasMoreComments,
        appState: action.data.appState,
      };
    }

    case 'update-app-state': {
      return {
        ...state,
        appState: action.data,
      };
    }

    case 'update-sort-order': {
      return {
        ...state,
        sortOrder: action.data,
        comments: INITIAL_CONTEXT.comments,
        nextToken: INITIAL_CONTEXT.nextToken,
        limit: INITIAL_CONTEXT.limit,
        hasMoreComments: INITIAL_CONTEXT.hasMoreComments,
        activeReplyForm: INITIAL_CONTEXT.activeReplyForm,
        activeReplyFormAfterLogin: INITIAL_CONTEXT.activeReplyFormAfterLogin,
      };
    }

    case 'login-user': {
      return {
        ...state,
        user: {
          name: action.data.userName,
          token: action.data.accessToken,
          isLoggedIn: true,
        },
      };
    }

    case 'logout-user': {
      return {
        ...state,
        user: { name: '', token: '', isLoggedIn: false },
        activeReplyForm: null,
      };
    }

    case 'set-expand': {
      return {
        ...state,
        layout: { isExpanded: action.data },
      };
    }
    case 'update-thread': {
      return {
        ...state,
        thread: {
          state: action.data.state,
          authorUsername: action.data.authorUsername,
          welcomeMessage: action.data.welcomeMessage,
          goodbyeMessage: action.data.goodbyeMessage,
        },
      };
    }

    case 'update-active-reply-form': {
      return {
        ...state,
        activeReplyForm: action.data,
      };
    }

    case 'update-active-reply-form-after-login': {
      return {
        ...state,
        activeReplyFormAfterLogin: action.data,
      };
    }

    case 'set-srg-login-supported': {
      return {
        ...state,
        env: {
          ...state.env,
          isSrgLoginSupported: action.data,
        },
      };
    }

    default:
      // eslint-disable-next-line no-console
      console.error('Found no matching action in reducer. Action:', action);
      return state;
  }
};
